// src/pages/journal/JournalPage.tsx - Journal entries listing page

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Fab,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Psychology as PsychologyIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { fetchJournalEntries, deleteJournalEntry } from '../../store/slices/journalSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const JournalPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { entries, isLoading, error } = useSelector((state: RootState) => state.journal);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchJournalEntries(currentUser.uid));
    }
  }, [currentUser, dispatch]);
  
  const handleNewEntry = () => {
    navigate('/journal/entry');
  };
  
  const handleEditEntry = (entryId: string) => {
    navigate(`/journal/entry/${entryId}`);
  };
  
  const handleViewReflection = (entryId: string) => {
    navigate(`/journal/reflection/${entryId}`);
  };
  
  const handleDeleteEntry = async (entryId: string) => {
    if (currentUser) {
      await dispatch(deleteJournalEntry(entryId));
    }
  };
  
  const getMoodColor = (mood: string) => {
    switch (mood?.toLowerCase()) {
      case 'great':
        return 'success';
      case 'good':
        return 'info';
      case 'neutral':
        return 'default';
      case 'bad':
        return 'warning';
      case 'terrible':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  if (isLoading && entries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          Journal
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ width: '100%' }}>
        {entries.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No Journal Entries Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start tracking your thoughts, emotions, and progress by creating your first journal entry.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleNewEntry}
            >
              Create First Entry
            </Button>
          </Box>
        ) : (
          <List>
            {entries.map((entry, index) => (
              <React.Fragment key={entry.id}>
                <ListItem
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleEditEntry(entry.id!)}
                >
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          {formatDate(entry.timestamp)}
                        </Typography>
                        {entry.mood && (
                          <Chip 
                            label={entry.mood} 
                            size="small" 
                            color={getMoodColor(entry.mood) as any}
                            sx={{ ml: 2 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {entry.content}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    {entry.reflectionGenerated ? (
                      <IconButton 
                        edge="end" 
                        aria-label="view reflection"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewReflection(entry.id!);
                        }}
                        sx={{ mr: 1 }}
                      >
                        <PsychologyIcon color="primary" />
                      </IconButton>
                    ) : null}
                    <IconButton 
                      edge="end" 
                      aria-label="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEntry(entry.id!);
                      }}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      edge="end" 
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEntry(entry.id!);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < entries.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleNewEntry}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default JournalPage;
